import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { WaveSine, Plus, Star, PlugsConnected, UsersThree, Power } from "@phosphor-icons/react";
import CapacityOverview from "@/components/CapacityOverview";

const EMPTY = { name: "", host: "", kind: "FreeSWITCH", esl_port: "8021", capacity: "", cred_username: "", cred_secret: "" };

export default function MediaServers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [bdDlg, setBdDlg] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [threshold, setThreshold] = useState(80);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, cap] = await Promise.all([
        api.get("/servers/media"),
        api.get("/servers/capacity-overview"),
      ]);
      setRows(m.data);
      setBreakdown(cap.data.breakdown || []);
      setThreshold(cap.data.threshold_pct || 80);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEdit(s);
    setForm({ name: s.name, host: s.host, kind: s.kind, esl_port: String(s.esl_port || 8021),
      capacity: s.capacity ? String(s.capacity) : "", cred_username: s.cred_username || "", cred_secret: "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, host: form.host, kind: form.kind, esl_port: parseInt(form.esl_port || "8021"),
        capacity: form.capacity ? parseInt(form.capacity) : null,
        cred_username: form.cred_username || null };
      if (form.cred_secret) body.cred_secret = form.cred_secret;
      if (edit) await api.patch(`/servers/media/${edit.id}`, body);
      else await api.post("/servers/media", body);
      toast.success(edit ? "Server updated" : "Server added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, id) => { setBusy(id); try { await fn(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); } };
  const del = (id) => act(async () => { await api.delete(`/servers/media/${id}`); toast.success("Server removed"); }, id);
  const toggleStatus = (s) => act(async () => { await api.post(`/servers/media/${s.id}/status`, { status: s.status === "active" ? "inactive" : "active" }); toast.success("Status updated"); }, s.id);
  const setDefault = (s) => act(async () => { await api.post(`/servers/media/${s.id}/default`, { is_default: !s.is_default }); toast.success(s.is_default ? "Default cleared" : "Set as default"); }, s.id);
  const test = (s) => act(async () => { const { data } = await api.post(`/servers/media/${s.id}/test`); toast.success(`Reachable · ${data.latency_ms} ms (simulated)`); }, s.id);
  const bdFor = (sid) => breakdown.filter((b) => b.media_server_id === sid);

  return (
    <div>
      <PageHeader title="Media Servers" subtitle="FreeSWITCH media nodes assignable to customers">
        <Button onClick={openNew} data-testid="create-media-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Server</Button>
      </PageHeader>

      <CapacityOverview />

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="media-empty" icon={WaveSine} title="No media servers" description="Add FreeSWITCH media servers so you can assign them to customers." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="media-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Host</TableHead><TableHead>ESL Port</TableHead>
                <TableHead className="text-right">Capacity</TableHead><TableHead className="text-right">Active</TableHead>
                <TableHead>Default</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`media-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-sm">{s.host}</TableCell>
                    <TableCell className="tabular-nums">{s.esl_port || 8021}</TableCell>
                    <TableCell className="text-right tabular-nums">{s.capacity ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`media-active-${s.id}`}>{s.active_calls ?? 0}</TableCell>
                    <TableCell>
                      <button onClick={() => setDefault(s)} disabled={busy === s.id} data-testid={`media-default-${s.id}`}
                        className={`inline-flex items-center gap-1 text-xs ${s.is_default ? "text-amber-500" : "text-muted-foreground hover:text-foreground"}`}>
                        <Star className="w-4 h-4" weight={s.is_default ? "fill" : "regular"} /> {s.is_default ? "Default" : "Set"}
                      </button>
                    </TableCell>
                    <TableCell><StatusPill status={s.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => test(s)} disabled={busy === s.id} data-testid={`media-test-${s.id}`} title="Test connectivity"><PlugsConnected className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="rounded-lg px-2 gap-1" onClick={() => setBdDlg(s)} data-testid={`media-breakdown-${s.id}`} title="Per-customer breakdown">
                          <UsersThree className="w-4 h-4" />
                          <span className="tabular-nums text-xs" data-testid={`media-breakdown-count-${s.id}`}>{bdFor(s.id).length}</span>
                        </Button>
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => toggleStatus(s)} disabled={busy === s.id} data-testid={`media-toggle-${s.id}`} title={s.status === "active" ? "Deactivate" : "Activate"}><Power className="w-4 h-4" /></Button>
                        <Button variant="outline" size="sm" className="rounded-lg" onClick={() => openEdit(s)} data-testid={`edit-media-${s.id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => del(s.id)} data-testid={`delete-media-${s.id}`}>Delete</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="media-dialog">
          <DialogHeader><DialogTitle>{edit ? "Edit" : "Add"} media server</DialogTitle>
            <DialogDescription>Platform FreeSWITCH media node (assignable to customers).</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input data-testid="media-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2"><Label>Host / IP</Label><Input data-testid="media-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="10.0.1.10" /></div>
              <div className="space-y-2"><Label>ESL Port</Label><Input data-testid="media-esl-port" type="number" value={form.esl_port} onChange={(e) => setForm({ ...form, esl_port: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Kind</Label><Input data-testid="media-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} /></div>
              <div className="space-y-2"><Label>Capacity (max calls)</Label><Input data-testid="media-capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="500" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Credential username <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="media-cred-user" value={form.cred_username} onChange={(e) => setForm({ ...form, cred_username: e.target.value })} /></div>
              <div className="space-y-2"><Label>Credential secret <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="media-cred-secret" type="password" value={form.cred_secret} onChange={(e) => setForm({ ...form, cred_secret: e.target.value })} placeholder={edit?.has_cred_secret ? "•••••• (leave blank to keep)" : ""} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="submit-media-btn" className="rounded-lg">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!bdDlg} onOpenChange={(v) => !v && setBdDlg(null)}>
        <DialogContent className="max-w-3xl" data-testid="media-breakdown-dialog">
          <DialogHeader><DialogTitle>Per-customer breakdown — {bdDlg?.name}</DialogTitle>
            <DialogDescription>Customers assigned to this media node with their live call load and utilization.</DialogDescription></DialogHeader>
          <div className="py-2 max-h-[55vh] overflow-y-auto">
            {bdDlg && bdFor(bdDlg.id).length === 0 && <p className="text-sm text-muted-foreground">No customers assigned to this server.</p>}
            {bdDlg && bdFor(bdDlg.id).length > 0 && (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Customer calls</TableHead>
                  <TableHead className="text-right">Node calls</TableHead>
                  <TableHead className="text-right">Capacity</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Utilization</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bdFor(bdDlg.id).map((b) => (
                    <TableRow key={`${b.customer_id}`} data-testid={`media-bd-row-${b.customer_id}`}>
                      <TableCell className="font-medium">{b.customer_name}
                        {b.customer_status !== "active" && <span className="ml-2 text-xs text-muted-foreground">({b.customer_status})</span>}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.customer_active_calls}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.node_active_calls}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.capacity ?? "∞"}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.available ?? "∞"}</TableCell>
                      <TableCell className={`text-right tabular-nums ${b.utilization_pct >= threshold ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>{b.utilization_pct}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
